import "react-pdf/dist/Page/TextLayer.css";

import { memo, useRef, useEffect, useMemo } from "react";
import type { RefObject } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import pdfjsWorker from "pdfjs-dist/build/pdf.worker.min.mjs?url";

import { cn } from "@core/utils/cn";
import { PdfPageSkeleton } from "@modules/viewers/pdf/components/PdfPageSkeleton";
import { PdfErrorState } from "@modules/viewers/pdf/components/PdfErrorState";
import type { TPdfPageMeta } from "@modules/viewers/pdf/types";
import type { TSearchMatch } from "@modules/viewers/pdf/hooks/usePdfSearch";
import type { PdfPageMetrics } from "@core/types";

// ✅ IMPORTANT: worker setup
pdfjs.GlobalWorkerOptions.workerSrc = pdfjsWorker;

const OVERSCAN = 3;
const PAGE_ESTIMATE_WIDTH = 595;
const PAGE_ESTIMATE_HEIGHT = 842;
import { PAGE_GAP, PAGE_TOP_PADDING } from "@core/types";
const NAV_SUPPRESS_MS = 800;

type PdfMainViewerProps = {
  pdfFile: string | Blob;
  currentPage: number;
  totalPages: number;
  scale: number;
  rotation: number;
  searchMatches: TSearchMatch[];
  activeMatchIndex: number;
  onDocumentLoad: (pdfDoc: TPdfPageMeta) => void;
  onPageChange: (page: number) => void;
  mainViewerRef: RefObject<HTMLDivElement | null>;
  onPageMetricsChange?: (metrics: PdfPageMetrics) => void;
};

export const PdfMainViewer = memo(function PdfMainViewer({
  pdfFile,
  currentPage,
  totalPages,
  scale,
  rotation,
  searchMatches,
  activeMatchIndex,
  onDocumentLoad,
  onPageChange,
  mainViewerRef,
  onPageMetricsChange,
}: PdfMainViewerProps) {
  const originalPageWidthRef = useRef<number>(0);
  const originalPageHeightRef = useRef<number>(0);
  const lastReportedPageRef = useRef<number>(1);
  const rafIdRef = useRef<number | null>(null);

  const isNavigatingRef = useRef(false);
  const navTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const customTextRenderer = useMemo(() => {
    if (searchMatches.length === 0) return undefined;

    return ({
      pageNumber,
      itemIndex,
      str,
    }: {
      pageNumber: number;
      itemIndex: number;
      str: string;
    }) => {
      const itemMatches = searchMatches
        .map((m, globalIdx) => ({ ...m, globalIdx }))
        .filter((m) => m.page === pageNumber && m.itemIndex === itemIndex)
        .sort((a, b) => a.charOffset - b.charOffset);

      if (itemMatches.length === 0) return str;

      let result = "";
      let cursor = 0;

      for (const m of itemMatches) {
        result += str.slice(cursor, m.charOffset);
        const isActive = m.globalIdx === activeMatchIndex;
        result += `<mark class="${isActive ? "bg-yellow-400" : "bg-yellow-200"}">${str.slice(m.charOffset, m.charOffset + m.length)}</mark>`;
        cursor = m.charOffset + m.length;
      }

      result += str.slice(cursor);
      return result;
    };
  }, [searchMatches, activeMatchIndex]);

  useEffect(() => {
    originalPageWidthRef.current = 0;
    originalPageHeightRef.current = 0;
  }, [scale, rotation]);

  useEffect(() => {
    const container = mainViewerRef.current;
    if (!container) return;

    const ph =
      originalPageHeightRef.current > 0
        ? originalPageHeightRef.current * scale
        : PAGE_ESTIMATE_HEIGHT * scale;

    isNavigatingRef.current = true;
    if (navTimerRef.current !== null) clearTimeout(navTimerRef.current);
    navTimerRef.current = setTimeout(() => {
      isNavigatingRef.current = false;
    }, NAV_SUPPRESS_MS);

    container.scrollTo({
      top: (currentPage - 1) * (ph + PAGE_GAP) + PAGE_TOP_PADDING,
      behavior: "smooth",
    });
  }, [currentPage, scale, mainViewerRef]);

  useEffect(() => {
    const container = mainViewerRef.current;
    if (!container || totalPages === 0) return;

    const handleScroll = () => {
      if (isNavigatingRef.current) return;
      if (rafIdRef.current !== null) return;

      rafIdRef.current = requestAnimationFrame(() => {
        rafIdRef.current = null;

        const ph =
          originalPageHeightRef.current > 0
            ? originalPageHeightRef.current * scale
            : PAGE_ESTIMATE_HEIGHT * scale;

        const page = Math.max(
          1,
          Math.min(
            totalPages,
            Math.floor(
              (container.scrollTop - PAGE_TOP_PADDING) / (ph + PAGE_GAP),
            ) + 1,
          ),
        );

        if (page !== lastReportedPageRef.current) {
          lastReportedPageRef.current = page;
          onPageChange(page);
        }
      });
    };

    container.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      container.removeEventListener("scroll", handleScroll);
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
    };
  }, [onPageChange, mainViewerRef, totalPages, scale]);

  useEffect(() => {
    return () => {
      if (navTimerRef.current !== null) clearTimeout(navTimerRef.current);
      if (rafIdRef.current !== null) cancelAnimationFrame(rafIdRef.current);
    };
  }, []);

  const pageHeight =
    originalPageHeightRef.current > 0
      ? originalPageHeightRef.current * scale
      : PAGE_ESTIMATE_HEIGHT * scale;

  const skeletonWidth = Math.round(
    (originalPageWidthRef.current > 0
      ? originalPageWidthRef.current
      : PAGE_ESTIMATE_WIDTH) * scale,
  );
  const skeletonHeight = Math.round(
    (originalPageHeightRef.current > 0
      ? originalPageHeightRef.current
      : PAGE_ESTIMATE_HEIGHT) * scale,
  );

  const windowStart = totalPages > 0 ? Math.max(1, currentPage - OVERSCAN) : 1;
  const windowEnd =
    totalPages > 0 ? Math.min(totalPages, currentPage + OVERSCAN) : 1;
  const windowPages =
    totalPages > 0
      ? Array.from(
          { length: windowEnd - windowStart + 1 },
          (_, i) => windowStart + i,
        )
      : [];

  return (
    <div className={cn("relative bg-white")}>
      {totalPages > 0 && (
        <div
          style={{
            height: totalPages * (pageHeight + PAGE_GAP) + PAGE_TOP_PADDING * 2,
          }}
        />
      )}

      <div
        className={cn(
          "flex flex-col items-center gap-6",
          totalPages === 0 && "py-8",
        )}
        style={
          totalPages > 0
            ? {
                position: "absolute",
                top:
                  (windowStart - 1) * (pageHeight + PAGE_GAP) +
                  PAGE_TOP_PADDING,
                left: 0,
                right: 0,
              }
            : undefined
        }
      >
        <Document
          file={pdfFile}
          onLoadSuccess={onDocumentLoad}
          loading={
            <PdfPageSkeleton
              width={Math.round(PAGE_ESTIMATE_WIDTH * scale)}
              height={Math.round(PAGE_ESTIMATE_HEIGHT * scale)}
            />
          }
          error={<PdfErrorState />}
        >
          {windowPages.map((pageNum) => (
            <div key={pageNum}>
              <Page
                pageNumber={pageNum}
                scale={scale}
                rotate={rotation}
                renderTextLayer
                renderAnnotationLayer={false}
                customTextRenderer={customTextRenderer}
                loading={
                  <PdfPageSkeleton
                    width={skeletonWidth}
                    height={skeletonHeight}
                  />
                }
                onRenderSuccess={(page) => {
                  if (
                    originalPageHeightRef.current === 0 &&
                    page.originalHeight > 0
                  ) {
                    originalPageWidthRef.current = page.originalWidth;
                    originalPageHeightRef.current = page.originalHeight;
                    onPageMetricsChange?.({
                      originalPageWidth: page.originalWidth,
                      originalPageHeight: page.originalHeight,
                    });
                  }
                }}
              />
            </div>
          ))}
        </Document>
      </div>
    </div>
  );
});
